import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

/**
 * Left-navigation menu, served from the tenant's own database.
 *
 * The sidebar renders whatever comes back here. If the call fails, or the
 * tenant's table has not been seeded yet, the Sidebar falls back to its
 * bundled tree — navigation is the one surface that must never be empty.
 */

export type NavMenuNode = {
  id: string;
  item_key: string;
  label: string;
  /** SPA route relative to APP_BASE. `null` = container node; `""` is a
   *  real destination (the SPA index route the Dashboard sits on). */
  route: string | null;
  icon: string;
  badge: 'new' | 'soon' | string | null;
  /** Container nodes: render as an expandable dropdown. */
  is_collapsible: boolean;
  sort_order: number;
  allowed_roles: string[];
  is_active: boolean;
  is_system: boolean;
  entity_id: string | null;
  menu_scope: string;
  children: NavMenuNode[];
};

export type NavMenu = {
  scope: string;
  role: string;
  items: NavMenuNode[];
};

export function useNavMenu(entityId?: string | null) {
  return useQuery({
    queryKey: ['nav', 'menu', entityId || null],
    queryFn: () => api.get<NavMenu>('/nav/menu', {
      entity_id: entityId || undefined,
    }),
    // The menu changes when an admin edits it, which is rare. Keep it warm so
    // navigating between pages never re-fetches and flickers the sidebar.
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
}

export type ModuleAccess = {
  item_key: string;
  allowed: boolean;
  roles: string[];
};

/**
 * May the signed-in user open the module behind a nav item? The roles are
 * that menu item's `allowed_roles`, so an admin grants or withdraws access by
 * editing the menu — the module's API enforces the same list.
 */
export function useModuleAccess(itemKey: string) {
  return useQuery({
    queryKey: ['nav', 'access', itemKey],
    queryFn: () => api.get<ModuleAccess>(`/nav/access/${itemKey}`),
    staleTime: 5 * 60 * 1000,
  });
}
